import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Organization Top Bar - Persistent header for org dashboard
 *
 * Features:
 * - Org logo + name (left)
 * - Plan badge (Team/Business)
 * - Seat usage indicator
 * - Notification bell
 * - Owner avatar with dropdown
 */

export default function OrgTopBar({ organization, members, onMenuToggle, isMobileMenuOpen }) {
  const navigate = useNavigate();
  const { userProfile, user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  // Calculate seat usage
  const seatsUsed = members?.filter(m => m.status === 'active').length || 0;
  const totalSeats = organization?.seats || 10;
  const seatPercentage = Math.min((seatsUsed / totalSeats) * 100, 100);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl bg-[#1c1c1e]/90 border-b border-white/10">
      <div className="h-16 px-6 flex items-center justify-between">
        {/* Left: Mobile menu + Org info */}
        <div className="flex items-center gap-4">
          {/* Mobile menu toggle */}
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 rounded-xl hover:bg-white/10 transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Org Logo + Name */}
          <div className="flex items-center gap-3">
            {organization?.brandingLogo ? (
              <img
                src={organization.brandingLogo}
                alt={organization.name}
                className="w-9 h-9 rounded-xl object-cover"
              />
            ) : (
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                {organization?.name?.[0] || 'O'}
              </div>
            )}
            <div className="hidden sm:block">
              <h1 className="text-white font-semibold text-sm leading-tight">{organization?.name || 'Organization'}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                {/* Plan Badge */}
                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-gradient-to-r from-purple-500/30 to-blue-500/30 text-purple-300 border border-purple-500/30">
                  Team
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Center: Seat Usage (hidden on mobile) */}
        <div className="hidden md:flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-white/50 text-xs">Seats</span>
            <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  seatPercentage > 90 ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                  seatPercentage > 70 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                  'bg-gradient-to-r from-[#06c] to-purple-500'
                }`}
                style={{ width: `${seatPercentage}%` }}
              />
            </div>
            <span className="text-white/70 text-xs font-medium">{seatsUsed}/{totalSeats}</span>
          </div>
        </div>

        {/* Right: Notifications + Avatar */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2.5 rounded-xl hover:bg-white/10 transition-colors relative"
            >
              <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {/* Notification dot */}
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 top-12 w-80 backdrop-blur-xl bg-[#2c2c2e]/95 border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-white font-semibold text-sm">Notifications</h3>
                </div>
                <div className="p-2 max-h-80 overflow-y-auto">
                  <div className="text-center py-8">
                    <div className="text-white/30 text-sm">No new notifications</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* User Avatar Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-white/10 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center text-white font-medium text-sm">
                {userProfile?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
              </div>
              <svg className="w-4 h-4 text-white/50 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* User Dropdown */}
            {showDropdown && (
              <div className="absolute right-0 top-12 w-56 backdrop-blur-xl bg-[#2c2c2e]/95 border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
                <div className="p-4 border-b border-white/10">
                  <div className="text-white font-medium text-sm truncate">{userProfile?.displayName || 'User'}</div>
                  <div className="text-white/50 text-xs truncate">{user?.email}</div>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Personal Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/profile')}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </button>
                  <div className="my-2 border-t border-white/10" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
